<template>
    <div class="dw-10 f fv ath-form-radio-button-list" :key="uid">
        <!-- Label -->
        <div class="f fh fa-c dw-10 sm-b-xs" v-if="lbl">
            <slot name="labelLeft"></slot>
            <span class="fg ath-form-radio-button-list-label">{{ uiTitle }}<slot name="labelAfter"></slot></span>
            <slot name="labelRight"></slot>
        </div>
        <!-- Option list -->
        <label class="f fh fa-c sm-b-s"
            v-for="opt in opts"
            :for="opt.uid"
            :key="opt.uid"
            :class="[uiCursorOnOption && !disabled ? 'c-p' : '']">
            <div class="ath-form-radio-button-list-el"
                :class="[
                    'el--s-' + size,
                    opt.val === internal_value ? 'el--active' : '',
                    disabled ? 'c-na el--disabled' : 'c-p'
                ]">
                <!-- eslint-disable vue/no-mutating-props  -->
                <input class="u-hide"
                    type="radio"
                    :name="uid"
                    :id="opt.uid"
                    :value="opt.val"
                    :disabled="disabled || opt.disabled"
                    v-model="internal_value" />
                <span class="frm-bf c-el-panel-bg br-m pr ath-form-radio-button-list-el-visual"
                    :class="[disabled ? 'c-na' : 'c-p']"></span>
            </div>
            <slot name="optionLeft" :data="opt"></slot>
            <VisualIcon class="sm-r-xs" uiSize="m" :name="opt.icon" v-if="opt.icon" />
            <span class="fg sm-l-s te-note" v-if="opt.lbl">{{ opt.lbl | athT }}</span>
            <slot name="optionRight" :data="opt"></slot>
        </label>
        <slot name="after"></slot>
    </div>
</template>

<script>
import { guuid, isEqual, isNeString, isObject, join } from '@aretheyhappy/utils';
import VisualIcon from '../../atoms/visuals/Icon.vue';

export default {
    name: 'mead-form-radio-button-list',
    components: {VisualIcon},
    props: {
        /**
         * Label i18n key rendered above the radio list. Only shown when truthy.
         */
        lbl                 : {type: [String, Boolean], default: () => false},
        /**
         * Entity name interpolated into the label when translating it.
         */
        lblEntity           : {type: [Boolean, String], default: () => false},
        /**
         * Whether the interpolated entity name should be lowercased in the translated label.
         */
        lblEntityLc         : {type: Boolean, default: () => true},
        /**
         * Currently selected option value (v-model).
         */
        value               : {type: [String, Boolean], default: () => undefined},
        /**
         * Disables every radio option in the list.
         */
        disabled            : {type: Boolean, default: () => false},
        /**
         * Size modifier applied to each radio button visual (e.g. 's', 'm').
         */
        size                : {type: String, default: () => 'm'},
        /**
         * List of option objects (`{val, lbl, icon, disabled}`) rendered as individual radio buttons.
         */
        options             : {type: Array, default: () => []},
        /**
         * Appends an "(optional)" suffix to the label.
         */
        uiOptional          : {type: Boolean, default: () => false},
        /**
         * Whether to show a pointer cursor over options that aren't disabled.
         */
        uiCursorOnOption    : {type: Boolean, default: () => true},
    },
    data () {
        return {
            uid: guuid(),
            internal_value: this.value,
        };
    },
    watch: {
        internal_value: function (new_val, old_val) {
            if (isEqual(new_val, old_val)) return;
            this.$emit('input', this.internal_value);
        },
        value: function () {
            this.internal_value = this.value
        },
    },
    computed: {
        opts : function () {
            return this.options.reduce((acc, el) => {
                if (
                    !isObject(el) ||
                    !el.hasOwnProperty('val') ||
                    !isNeString(el.lbl)
                ) return acc;

                acc.push(Object.assign({uid: guuid(), disabled: false}, el));
                return acc;
            }, []);
        },
        uiTitle: function () {
            return join([
                this.lblEntity
                    ? MeadCommon.I18N[this.lblEntityLc ? 'actionLc' : 'action'](this.lbl, this.lblEntity)
                    : MeadCommon.I18N.go(this.lbl),
                this.uiOptional
                    ? `(${MeadCommon.I18N.go('mead.labels.optional')})`
                    : false,
            ], {delim: ' '});
        },
    },
};
</script>

<style lang="scss" scoped>
@import '../../../../sass/_variables.scss';

.ath-form-radio-button-list-label {
    font-family: $ff-b;
    font-weight: 400;
    line-height: $lh-xs;
    font-size: $fs-s;
    color: var(--c-txt);
}

.ath-form-radio-button-list-el-visual {
    display: block;
    width: 2rem;
    height: 2rem;
    font-weight: 600;
    pointer-events: none;

    &:after {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        display: inline-block;
        content: "";
        opacity: 0;
    }
}

.ath-form-radio-button-list-el {
    &.is--active .ath-form-radio-button-list-el-visual:after,
    input[type="radio"]:checked + .ath-form-radio-button-list-el-visual:after {
        opacity: 1;
        background-color: var(--c-brand-active-bg);
        border-radius: 50%;
        margin: $s-xs;
    }

    &.is--disabled .ath-form-radio-button-list-el-visual,
    input[type="radio"]:disabled + .ath-form-radio-button-list-el-visual {
        opacity: .65;
        filter: grayscale(1);
    }

    //  SIZES

    @mixin elFrmRadioSize ($name, $dim) {
        &.el--s-#{$name} .ath-form-radio-button-list-el-visual {
            width: $dim;
            height: $dim;
        }
    }

    @include elFrmRadioSize('s', 2rem);
    @include elFrmRadioSize('m', 3rem);
}
</style>
